import React, { Fragment, useEffect, useState } from "react";
import { ShowHide } from "components/layout";
import { AlertUnsavedData, NotificationInline } from "components/notifications";
import { UserAccess, UserHasAccess } from "components/user-access";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PageRestricted } from "pages/errors/page-restricted";
import PropTypes from "prop-types";
import { 
    DefaultEventTypeProcessModel, DefaultEventTypeSettingsModel, 
    DefaultRequiredCommentOptions, FailedOnlyCommentOption,
    DefaultRequiredDefectOptions, FailedOnlyDefectOption
} from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";
import { FormNumericInput, FormRadio, FormSwitch, FormTextArea } from "components/form-fields";
import { ButtonSave } from "components/buttons";
import { useNavigate } from "react-router-dom";

export function EventTypeSettings({
    model = DefaultEventTypeSettingsModel,
    forceLoad = false,
    processModel = DefaultEventTypeProcessModel,
    isInWizardMode = true,
    saving = false,
    globalLoading = false,
    onValidationUpdate = () => { },
    onModelUpdate = () => { return DefaultEventTypeSettingsModel; },
    onSave = () => { },
    onForceLoadFinished = () => { },
    erMaintenanceLocked = false
}) {
    var navigate = useNavigate();

    const step = 3;
    const fmsManageAction = "FMS:Manage";
    const userHasFmsManageAction = UserHasAccess(fmsManageAction);

    const [loading, setLoading] = useState(true);
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);

    const [originalSettingsModel, setOriginalSettingsModel] = useState(DefaultEventTypeSettingsModel);
    const [settingsModel, setSettingsModel] = useState(DefaultEventTypeSettingsModel)

    const [isValid, settingsModelErrors, validateModel] = useValidation();
    const isSettingsModelDirty = useDirtyData(originalSettingsModel, settingsModel);

    const [requireCommentOptions, setRequireCommentOptions] = useState([...DefaultRequiredCommentOptions]);
    const [requireDefectOptions, setRequireDefectOptions] = useState([...DefaultRequiredDefectOptions]);

    // Field names
    const includeCommentFieldName = "includeComment";
    const requireCommentValueFieldName = "requireCommentValue";
    const requiresCommentFieldName = "requiresComment";
    const requiresCommentFailedOnlyFieldName = "requiresCommentFailedOnly";
    const commentHelperTextFieldName = "commentHelperText";
    const includeDefectFieldName = "includeDefect";
    const requireDefectValueFieldName = "requireDefectValue";
    const requiresDefectFieldName = "requiresDefect";
    const requiresDefectFailedOnlyFieldName = "requiresDefectFailedOnly";
    const defectHelperTextFieldName = "defectHelperText";
    const includeDocumentationFieldName = "includeDocumentation";
    const includeDocumentationFailedOnlyFieldName = "includeDocumentationFailedOnly";
    const requiresDocumentationCountFieldName = "requiresDocumentationCount";
    const documentationHelperTextFieldName = "documentationHelperText";
    // FieldNames END

    const switchFields = [includeCommentFieldName, includeDefectFieldName, includeDocumentationFieldName, includeDocumentationFailedOnlyFieldName];
    const numericFields = [requiresDocumentationCountFieldName];
    const failedOnlyDocumentsMinimumDocCount = 1;
    const [minimumRequiredDocumentationCount, setMinimumRequiredDocumentationCount] = useState(0);

    // When the event type is ER maintenance PMI, certain fields are locked to comply with DVSA Earned Recognition requirements.
    const erMaintenanceLockMessage = "Field locked to comply with requirements of the DVSA Earned Recognition scheme";

    useEffect(load, [model, forceLoad]);

    function load() {
        if (!userHasFmsManageAction || (forceLoad === false && initialLoadCompleted)) {
            return;
        }

        setModel(prev => { return {...prev, ...model}; });
        
        if (
            !isInWizardMode &&
            (
                model.includeComment !== DefaultEventTypeSettingsModel.includeComment ||
                model.requireCommentValue !== DefaultEventTypeSettingsModel.requireCommentValue ||
                model.requiresComment !== DefaultEventTypeSettingsModel.requiresComment ||
                model.requiresCommentFailedOnly !== DefaultEventTypeSettingsModel.requiresCommentFailedOnly ||
                model.commentHelperText !== DefaultEventTypeSettingsModel.commentHelperText ||
                model.includeDefect !== DefaultEventTypeSettingsModel.includeDefect ||
                model.requireDefectValue !== DefaultEventTypeSettingsModel.requireDefectValue ||
                model.requiresDefect !== DefaultEventTypeSettingsModel.requiresDefect ||
                model.requiresDefectFailedOnly !== DefaultEventTypeSettingsModel.requiresDefectFailedOnly ||
                model.defectHelperText !== DefaultEventTypeSettingsModel.defectHelperText ||
                model.includeDocumentationValue !== DefaultEventTypeSettingsModel.includeDocumentationValue ||
                model.includeDocumentation !== DefaultEventTypeSettingsModel.includeDocumentation ||
                model.includeDocumentationFailedOnly !== DefaultEventTypeSettingsModel.includeDocumentationFailedOnly ||
                model.requiresDocumentationCount !== DefaultEventTypeSettingsModel.requiresDocumentationCount ||
                model.documentationHelperText !== DefaultEventTypeSettingsModel.documentationHelperText
            )
        ) {
            setOriginalSettingsModel(model);
        }
        
        setInitialLoadCompleted(true);

        tryAddFailedStatusOptions();

        if (forceLoad) {
            onForceLoadFinished();
        }
    }

    useEffect(() => {
        setLoading(globalLoading);
    }, [globalLoading]);

    // publish out validation changes if in wizard mode
    useEffect(() => {
        if (!isInWizardMode) {
            return;
        }

        onValidationUpdate(step, isValid);
    }, [isValid])

    useEffect(validate, [settingsModel])

    function validate() {
        let validationRules = [
            { 
                fieldName: commentHelperTextFieldName,
                maxLength: 1000 
            },
            {
                fieldName: defectHelperTextFieldName,
                maxLength: 1000
            },
            {
                fieldName: documentationHelperTextFieldName,
                maxLength: 1000 
            },
            { 
                fieldName: requiresDocumentationCountFieldName, 
                required: showDocumentationFields(),
                minValue: minimumRequiredDocumentationCount, 
                maxValue: 50, 
                type: 'number'
            }
        ];

        validateModel(validationRules, settingsModel);
    }

    function tryAddFailedStatusOptions() {
        const tempRequireCommentOptions = [...DefaultRequiredCommentOptions];
        const tempRequireDefectOptions = [...DefaultRequiredDefectOptions];

        if (processModel.failStatusEnabled) {
            tempRequireCommentOptions.push(FailedOnlyCommentOption);
            tempRequireDefectOptions.push(FailedOnlyDefectOption);
        }

        setRequireCommentOptions(tempRequireCommentOptions);
        setRequireDefectOptions(tempRequireDefectOptions);
    }

    // Use this to handle input value changes
    function onValueChange(fieldName, item) {
        let value = null;

        if (switchFields.includes(fieldName)) {
            value = item.currentTarget.checked;
        } 
        else if (numericFields.includes(fieldName)) {
            value = item;
        } else {
            value = item.currentTarget.value;
        }

        const clonedModel = {
            ...settingsModel,
            [fieldName]: value
        };

        if (
            clonedModel.includeDocumentation === true && 
            clonedModel.includeDocumentationFailedOnly === true
        ) {
            // only default the count if it doesn't have a value or is 0 or less
            if (clonedModel?.requiresDocumentationCount === null || clonedModel?.requiresDocumentationCount === undefined || clonedModel?.requiresDocumentationCount <= 0) {
                clonedModel[requiresDocumentationCountFieldName] = failedOnlyDocumentsMinimumDocCount;
            }
            setMinimumRequiredDocumentationCount(failedOnlyDocumentsMinimumDocCount);
        } else {
            setMinimumRequiredDocumentationCount(0);
        }

        setSettingsModel(clonedModel);
        onModelUpdate(step, clonedModel);
    }

    function onRadioFieldChange(fieldName, item) {
        const clonedModel = { ...settingsModel }

        if (fieldName === requireCommentValueFieldName) {
            const selectedRequireCommentValue = requireCommentOptions.find(o => o.value === item.currentTarget.value);

            clonedModel[requireCommentValueFieldName] = selectedRequireCommentValue.value;
            clonedModel[requiresCommentFieldName] = selectedRequireCommentValue.requiredComments;
            clonedModel[requiresCommentFailedOnlyFieldName] = selectedRequireCommentValue.requiredCommentsFailedOnly;
        }

        if (fieldName === requireDefectValueFieldName) {
            const selectedRequireDefectValue = requireDefectOptions.find(o => o.value === item.currentTarget.value);

            clonedModel[requireDefectValueFieldName] = selectedRequireDefectValue.value;
            clonedModel[requiresDefectFieldName] = selectedRequireDefectValue.requiredDefects;
            clonedModel[requiresDefectFailedOnlyFieldName] = selectedRequireDefectValue.requiredDefectsFailedOnly;
        }

        setSettingsModel(clonedModel);
        onModelUpdate(step, clonedModel);
    }

    function setModel(changeModel) {
        setOriginalSettingsModel(changeModel);
        setSettingsModel(changeModel);
    }

    function showCommentFields() {
        return settingsModel.includeComment === true;
    }

    function showDefectFields() {
        return settingsModel.includeDefect === true;
    }

    function showDocumentationFields() {
        return settingsModel.includeDocumentation === true;
    }

    function showDocumentFailStatusField() {
        return settingsModel.includeDocumentation === true && processModel.failStatusEnabled === true;
    }

    function onConfirmLeave(redirectToPath) {
        setModel(originalSettingsModel);
        onModelUpdate(step, originalSettingsModel);

        setTimeout(() => navigate(redirectToPath));
    }

    return (
        <UserAccess perform={fmsManageAction}
            yes={() => (
                <Fragment>
                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <Fragment>
                                <h2>Event Settings</h2>
                                <AlertUnsavedData isDirty={isSettingsModelDirty} onConfirmLeave={onConfirmLeave} />
                            </Fragment>
                        )}
                    />

                    <h3>Comment</h3>
                    
                    <FormSwitch
                        disabled={saving}
                        loading={loading}
                        headingText="Enable comment"
                        checked={settingsModel.includeComment}
                        onChange={(item) => onValueChange(includeCommentFieldName, item)}
                    />

                    <ShowHide
                        evaluator={showCommentFields()}
                        show={(
                            <Fragment>
                                <FormRadio
                                    disabled={saving}
                                    options={requireCommentOptions}
                                    onChange={(item) => onRadioFieldChange(requireCommentValueFieldName, item)}
                                    headingText="Make comment required"
                                    selectedValue={settingsModel.requireCommentValue}
                                    dangerHelperText={settingsModelErrors[requireCommentValueFieldName]}
                                    loading={loading}
                                    id="input-requires-comment"
                                />

                                <FormTextArea
                                    maxLength={1000}
                                    disabled={saving}
                                    loading={loading}
                                    onChange={(item) => onValueChange(commentHelperTextFieldName, item)}
                                    headingText="Comment helper text"
                                    value={settingsModel.commentHelperText}
                                    dangerHelperText={settingsModelErrors[commentHelperTextFieldName]}
                                />
                            </Fragment>
                        )}
                    />

                    <h3>Event Defects</h3>

                    <FormSwitch
                        disabled={saving || erMaintenanceLocked}
                        loading={loading}
                        headingText="Enable defects"
                        checked={settingsModel.includeDefect}
                        onChange={(item) => onValueChange(includeDefectFieldName, item)}
                    />

                    <NotificationInline
                        allowClose={false}
                        text={erMaintenanceLockMessage}
                        intent="warning"
                        show={erMaintenanceLocked}
                    />

                    <ShowHide
                        evaluator={showDefectFields()}
                        show={(
                            <Fragment>
                                <FormRadio
                                    disabled={saving}
                                    options={requireDefectOptions}
                                    onChange={(item) => onRadioFieldChange(requireDefectValueFieldName, item)}
                                    headingText="Make defect required"
                                    selectedValue={settingsModel.requireDefectValue}
                                    dangerHelperText={settingsModelErrors[requireDefectValueFieldName]}
                                    loading={loading}
                                    id="input-requires-defect"
                                />

                                <FormTextArea
                                    maxLength={1000}
                                    disabled={saving}
                                    loading={loading}
                                    onChange={(item) => onValueChange(defectHelperTextFieldName, item)}
                                    headingText="Defect helper text"
                                    value={settingsModel.defectHelperText}
                                    dangerHelperText={settingsModelErrors[defectHelperTextFieldName]}
                                />
                            </Fragment>
                        )}
                    />

                    <h3>Event Documents</h3>
                    
                    <FormSwitch
                        disabled={saving}
                        loading={loading}
                        headingText="Enable document attachments"
                        checked={settingsModel.includeDocumentation}
                        onChange={(item) => onValueChange(includeDocumentationFieldName, item)}
                    />

                    <ShowHide
                        evaluator={showDocumentationFields()}
                        show={(
                            <Fragment>
                                <ShowHide
                                    evaluator={showDocumentFailStatusField()}
                                    show={(
                                        <FormSwitch
                                            disabled={saving}
                                            loading={loading}
                                            headingText="Only required for failed events"
                                            checked={settingsModel.includeDocumentationFailedOnly}
                                            onChange={(item) => onValueChange(includeDocumentationFailedOnlyFieldName, item)}
                                        />
                                    )}
                                />

                                <FormNumericInput
                                    id="numeric-input-required-documentation-count"
                                    disabled={saving}
                                    disabledInput={saving}
                                    headingText="Set minimum number of required documents"
                                    helperText="If documentation is optional then set to 0"
                                    onValueChange={(item) => onValueChange(requiresDocumentationCountFieldName, item)}
                                    selectedValue={settingsModel.requiresDocumentationCount}
                                    loading={loading}
                                    min={minimumRequiredDocumentationCount}
                                    max={50}
                                    dangerHelperText={settingsModelErrors[requiresDocumentationCountFieldName]}
                                />

                                <FormTextArea
                                    maxLength={1000}
                                    disabled={saving}
                                    loading={loading}
                                    onChange={(item) => onValueChange(documentationHelperTextFieldName, item)}
                                    headingText="Document attachment helper text"
                                    value={settingsModel.documentationHelperText}
                                    id="textarea-documentation-helper-text"
                                    dangerHelperText={settingsModelErrors[documentationHelperTextFieldName]}
                                />
                            </Fragment>
                        )}
                    />

                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <ButtonSave
                                loading={loading}
                                onClick={() => onSave("settings")}
                                disabled={saving}
                                simpleDisabled={!isSettingsModelDirty || !isValid}
                            />	
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EventTypeSettings.propTypes = {
    model: PropTypes.object,
    forceLoad: PropTypes.bool,
    processModel: PropTypes.object,
    isInWizardMode: PropTypes.bool,
    saving: PropTypes.bool.isRequired,
    globalLoading: PropTypes.bool,
    onValidationUpdate: PropTypes.func,
    onModelUpdate: PropTypes.func,
    onSave: PropTypes.func,
    onForceLoadFinished: PropTypes.func,
    erMaintenanceLocked: PropTypes.bool,
};